import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import FormField from "../../../components/ui/FormField";
import { Button } from "../../../components/Button";
import { ChG7RegisterApi } from "../../../api/Ch7RegisterApi";
import { ArrowLeft, Trash2 } from "lucide-react";

const mapEntryToDTO = (entry) => ({
  chargeId: entry.chargeId || null,
  dateOfCreationOrAcquisition: entry.creationDate || null,
  dateOfRegistrationOfCreation: entry.regCreationDate || null,
  propertyDescription: entry.propertyDesc || null,
  periodAndAmountSecured: entry.periodAmount || null,
  chargeHolderNameAndAddress: entry.holderDetails || null,
  termsAndConditions: entry.terms || null,
  instrumentCreatingChargeDescription: entry.instrumentCreate || null,
  dateOfModification: entry.modificationDate || null,
  dateOfRegistrationOfModification: entry.modRegDate || null,
  instrumentModifyingChargeDescription: entry.instrumentModify || null,
  particularsOfModification: entry.modificationDetails || null,
  dateOfSatisfaction: entry.satisfactionDate || null,
  dateOfRegistrationOfSatisfaction: entry.satRegDate || null,
  factsAndDateOfCondonation: entry.condonation || null,
  reasonsForDelay: entry.delayReason || null,
});

const createDefaultEntry = () => ({ id: Date.now() + Math.random() });

const ChgSevenForm = () => {
  const token = useSelector((state) => state.auth.access_token);
  const companies = useSelector((state) => state.account.accountDetails);

  const [accountId, setAccountId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [companyInfo, setCompanyInfo] = useState(() => {
    const saved = localStorage.getItem("chg7_company_info");
    return saved ? JSON.parse(saved) : { place: "", date: "", signature: "" };
  });

  const [entries, setEntries] = useState(() => {
    const saved = localStorage.getItem("chg7_register_data");
    return saved ? JSON.parse(saved) : [createDefaultEntry()];
  });

  useEffect(() => {
    localStorage.setItem("chg7_register_data", JSON.stringify(entries));
  }, [entries]);

  useEffect(() => {
    localStorage.setItem("chg7_company_info", JSON.stringify(companyInfo));
  }, [companyInfo]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCompanyChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "") {
      setAccountId("");
      return;
    }
    const selectedCompany = companies.find(
      (company) => String(company.id) === String(selectedValue)
    );
    if (selectedCompany) {
      setAccountId(selectedCompany.id);
    } else {
      console.error("Company not found for value:", selectedValue);
    }
  };

  const handleCompanyInfoChange = (field, value) => {
    setCompanyInfo((prev) => ({ ...prev, [field]: value }));
  };

  const updateField = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, createDefaultEntry()]);
  };

  const deleteEntry = (id) => {
    if (entries.length > 1) {
      setEntries((prev) => prev.filter((e) => e.id !== id));
    }
  };

  // ── Save / Submit ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!accountId) {
      setSubmitStatus({
        type: "error",
        message: "Please select a company before saving.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Build payload matching the Postman JSON structure exactly
      const payload = {
        place: companyInfo.place || null,
        date: companyInfo.date || null,
        signature: companyInfo.signature || null,
        registerOfChargeEntries: entries.map(mapEntryToDTO),
      };

      await ChG7RegisterApi.submitCh7Form(payload, accountId, token);

      setSubmitStatus({
        type: "success",
        message: "CHG-7 Register data saved successfully!",
      });

      // Clear localStorage after successful save
      localStorage.removeItem("chg7_register_data");
      localStorage.removeItem("chg7_company_info");

      // Reset form state
      setEntries([createDefaultEntry()]);
      setCompanyInfo({ place: "", date: "", signature: "" });
      setAccountId("");
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error.message ||
          "Failed to save CHG-7 register data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Form Header */}
      <div className="bg-white px-3 py-2 mb-2 border-b-4 shadow-sm rounded-xl relative text-center">
        <button
          onClick={() => window.history.back()}
          className="absolute top-3 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-2xl text-gray-900 uppercase tracking-tight">
          Form No. CHG - 7
        </h1>
        <h2 className="text-lg font-semibold">Register of Charges</h2>
        <p className="text-[10px] italic text-slate-500 mt-2">
          [Pursuant to section 85 and sub-rule (1) of rule 10 of the Companies
          (Registration of Charges) Rules, 2014]
        </p>
      </div>

      {/* Company Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name of the company *
        </label>
        <select
          value={accountId}
          onChange={handleCompanyChange}
          className="w-50 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select a company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.accountName || `Company ${company.id}`}
            </option>
          ))}
        </select>
      </div>

      {/* Status Message */}
      {submitStatus && (
        <div
          className={`mb-3 px-4 py-3 rounded-lg text-sm font-medium border ${submitStatus.type === "success"
              ? "bg-green-50 text-green-800 border-green-200"
              : "bg-red-50 text-red-800 border-red-200"
            }`}
        >
          {submitStatus.message}
        </div>
      )}

      {/* Charge Entries */}
      <div className="space-y-2">
        {entries.map((entry, index) => (
          <div
            key={entry.id}
            className="bg-white border border-slate-200 rounded-xl shadow-md overflow-hidden"
          >
            <div className="bg-primary text-white px-3 py-2 flex justify-between items-center">
              <span className="font-semibold text-sm uppercase text-white tracking-widest">
                Charge Entry No: {index + 1}
              </span>
              {entries.length > 1 && (
                <button
                  onClick={() => deleteEntry(entry.id)}
                  className="text-red-600 bg-white p-1 rounded-md"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>

            <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <FormField
                label="Charge ID"
                value={entry.chargeId}
                onChange={(v) => updateField(entry.id, "chargeId", v)}
              />
              <FormField
                label="Date of creation of charge or acquisition"
                type="date"
                value={entry.creationDate}
                onChange={(v) => updateField(entry.id, "creationDate", v)}
              />
              <FormField
                label="Date of registration of creation of charge"
                type="date"
                value={entry.regCreationDate}
                onChange={(v) => updateField(entry.id, "regCreationDate", v)}
              />
              <FormField
                label="Short description of property charged"
                type="description"
                value={entry.propertyDesc}
                onChange={(v) => updateField(entry.id, "propertyDesc", v)}
              />
              <FormField
                label="Period and amount secured by the charge"
                type="description"
                value={entry.periodAmount}
                onChange={(v) => updateField(entry.id, "periodAmount", v)}
              />
              <FormField
                label="Names & addresses of charge holder"
                type="description"
                value={entry.holderDetails}
                onChange={(v) => updateField(entry.id, "holderDetails", v)}
              />
              <FormField
                label="Particulars of the terms and conditions of the charge"
                type="description"
                value={entry.terms}
                onChange={(v) => updateField(entry.id, "terms", v)}
              />
              <FormField
                label="Description of the instrument creating the charge"
                type="description"
                value={entry.instrumentCreate}
                onChange={(v) => updateField(entry.id, "instrumentCreate", v)}
              />
              <FormField
                label="Date of modification of charge"
                type="date"
                value={entry.modificationDate}
                onChange={(v) => updateField(entry.id, "modificationDate", v)}
              />
              <FormField
                label="Date of registration of modification of charge"
                type="date"
                value={entry.modRegDate}
                onChange={(v) => updateField(entry.id, "modRegDate", v)}
              />
              <FormField
                label="Description of the instrument modifying the charge"
                type="description"
                value={entry.instrumentModify}
                onChange={(v) => updateField(entry.id, "instrumentModify", v)}
              />
              <FormField
                label="Particulars of modification"
                type="description"
                value={entry.modificationDetails}
                onChange={(v) =>
                  updateField(entry.id, "modificationDetails", v)
                }
              />
              <FormField
                label="Date of satisfaction"
                type="date"
                value={entry.satisfactionDate}
                onChange={(v) => updateField(entry.id, "satisfactionDate", v)}
              />
              <FormField
                label="Registration date of satisfaction"
                type="date"
                value={entry.satRegDate}
                onChange={(v) => updateField(entry.id, "satRegDate", v)}
              />
              <FormField
                label="Facts and date of condonation of delay, if any"
                type="description"
                value={entry.condonation}
                onChange={(v) => updateField(entry.id, "condonation", v)}
              />
              <FormField
                label="Reasons for delay in filing for registration of creation, modification or satisfaction of the charge, if any"
                type="description"
                value={entry.delayReason}
                onChange={(v) => updateField(entry.id, "delayReason", v)}
              />
            </div>
          </div>
        ))}

        <div className="flex flex-row justify-end">
          <Button
            label="Add New Charge Entry"
            onClick={addEntry}
            className="text-sm"
          />
        </div>
      </div>

      {/* Footer — Place / Date / Signature */}
      <div className="mt-2 bg-white p-3 rounded-lg shadow grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FormField
          label="Place"
          value={companyInfo.place}
          onChange={(v) => handleCompanyInfoChange("place", v)}
        />
        <FormField
          label="Date"
          type="date"
          value={companyInfo.date}
          onChange={(v) => handleCompanyInfoChange("date", v)}
        />
        <FormField
          label="Signature (MD/Director)"
          value={companyInfo.signature}
          onChange={(v) => handleCompanyInfoChange("signature", v)}
        />
      </div>

      {/* Save Button */}
      <div className="flex flex-row justify-end items-center">
        <Button
          label={isSubmitting ? "Saving..." : "Save CHG-7 Register"}
          className="my-2 bg-green-600 hover:bg-green-700"
          onClick={handleSave}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
};

export default ChgSevenForm;